<template lang="pug">
md-layout(md-flex='100')
  md-layout(md-flex='90', md-flex-offset='5')
    md-card.md-table-card.content-table
      md-card-header
        .header-row
          h2.md-title Reporting Dashboard
          .header-right
            .period-control
              span.period-label-text Period:
              md-button.md-icon-button(@click='prevPeriod', :disabled='loading || generating')
                md-icon(md-iconset='fa fa-chevron-left')
              md-menu(md-direction='bottom-end', md-size='auto')
                md-button.md-icon-button(md-menu-trigger)
                  md-icon(md-iconset='fa fa-calendar-alt')
                md-menu-content
                  .period-picker-popup
                    md-input-container
                      label Month
                      md-select(v-model='filters.month', @selected='onPeriodChanged')
                        md-option(:value='m.value', v-for='m in months', :key='m.value') {{ m.name }}
                    md-input-container
                      label Year
                      md-select(v-model='filters.year', @selected='onPeriodChanged')
                        md-option(:value='y.value', v-for='y in years', :key='y.value') {{ y.value }}
              span.current-period-label {{ currentPeriodLabel }}
              md-button.md-icon-button(@click='nextPeriod', :disabled='loading || generating')
                md-icon(md-iconset='fa fa-chevron-right')
            md-button.md-raised.md-primary(
              v-if='isSuperAdmin()'
              @click='generateReport'
              :disabled='generating || !canSearch'
            )
              md-icon.on-left(md-iconset='fa fa-sync-alt')
              | {{ generating ? 'Generating...' : 'Generate / Regenerate' }}

      .role-tabs
        button.role-tab(:class="{ active: activeRole === 'content-owner' }", @click='setRole("content-owner")') CONTENT OWNER
        button.role-tab(:class="{ active: activeRole === 'buyer' }", @click='setRole("buyer")') BUYER

      //- KPI Summary — loader covers only the summary cards + "no report" state
      stitch-loader(:loading='loading', :size='45')
        div(v-if='report && isContentOwnerRole')
          .summary-row
            .summary-card
              .summary-label LICENSING GROSS REVENUE
              .summary-value {{ formatCurrency(summary.licensingGross) }}
            .summary-card
              .summary-label RHEBUS COMMISSION - LICENSING
              .summary-value {{ formatCurrency(summary.rhebusCommission) }}
            .summary-card
              .summary-label LICENSING NET REVENUE
              .summary-value {{ formatCurrency(summary.netEarnings) }}
            .summary-card
              .summary-label RENTAL NET REVENUE
              .summary-value {{ formatCurrency(rentalTotals.netEarnings) }}
            .summary-card
              .summary-label TOTAL NET REVENUE
              .summary-value {{ formatCurrency(summary.totalWithRentals) }}
        div(v-if='isBuyerRole')
          .summary-row
            .summary-card
              .summary-label TOTAL LICENSING COST
              .summary-value {{ formatCurrency(buyerSummary.totalLicensingCost) }}
            .summary-card
              .summary-label TOTAL PLAYS
              .summary-value {{ buyerSummary.totalPlays.toLocaleString() }}
            .summary-card
              .summary-label VIDEOS LICENSED
              .summary-value {{ buyerSummary.videosLicensed.toLocaleString() }}
            .summary-card
              .summary-label AVERAGE CPM
              .summary-value {{ formatCurrency(buyerSummary.averageCpm) }}
        md-layout(v-if='isContentOwnerRole && !report && !loading', md-align='center')
          .no-data
            md-icon.md-accent(md-iconset='fa fa-exclamation-circle')
            span No report found for the selected period. Use the Generate button to create one.

      //- Search and filter — always visible, never covered by any loader
      .licensing-search-filter-row
        md-input-container.search-input-container
          md-input(
            v-model='licensingSearchText'
            placeholder='Search by video name...'
          )
        .licensee-filter(v-if='isContentOwnerRole && licenseeOptions.length > 0')
          span.licensee-label Licensee:
          md-input-container
            md-select(v-model='filters.licenseeAccountId', @selected='applyLicenseeFilter')
              md-option(value='') All Licensees
              md-option(:value='l.id', v-for='l in licenseeOptions', :key='l.id') {{ l.name }}
        .licensee-filter(v-if='isContentOwnerRole && distributionOptions.length > 0')
          span.licensee-label Distribution:
          md-input-container
            md-select(v-model='filters.distribution', @selected='applyLicenseeFilter')
              md-option(value='') All Distributions
              md-option(:value='d.id', v-for='d in distributionOptions', :key='d.id') {{ d.name }}
        .licensee-filter(v-if='isBuyerRole && buyerContentOwners.length > 0')
          span.licensee-label Content Owner:
          md-input-container
            md-select(v-model='filters.contentOwner', @selected='applyBuyerFilters')
              md-option(value='') All Content Owners
              md-option(:value='owner', v-for='owner in buyerContentOwners', :key='owner') {{ owner }}
        .licensee-filter(v-if='isBuyerRole && buyerDistributions.length > 0')
          span.licensee-label Distribution:
          md-input-container
            md-select(v-model='filters.distribution', @selected='applyBuyerFilters')
              md-option(value='') All Distributions
              md-option(:value='dist.id', v-for='dist in buyerDistributions', :key='dist.id') {{ dist.name }}

      //- Licensing Revenue — has its own loader so search bar stays visible during fetch
      h3.section-title {{ isBuyerRole ? 'Licensed Content Costs' : 'Licensing Revenue' }}
      stitch-loader(:loading='loadingLicensed', :size='45')
        .table(:class="{ 'buyer-table': isBuyerRole }", v-if='activeRows.length > 0')
          .titleLine.styleRow
            p.rowElements(
              v-for='col in activeColumns'
              :key='col.label'
              :class="{ sortable: !!col.sortKey }"
              @click='onColumnHeaderClick(col)'
            )
              span {{ col.label }}
              md-icon(v-if='col.sortKey', :md-iconset='sortIconFor(col.sortKey)')
          .styleRow.colorHover(v-for='(item, idx) in activeRows', :key='item._id || item.mediaItemId || idx')
            div.rowElements(v-for='col in activeColumns', :key='col.label') {{ col.getValue(item) }}
          .styleRow.totals-row(v-if='isContentOwnerRole')
            div.rowElements(style='font-weight:600') Totals
            div.rowElements
            div.rowElements
            div.rowElements {{ licensingTotals.plays.toLocaleString() }}
            div.rowElements {{ displayCurrency(licensingTotals.gross) }}
            div.rowElements {{ displayCurrency(licensingTotals.rhebusCommission) }}
            div.rowElements {{ displayCurrency(licensingTotals.net) }}
          .styleRow.totals-row(v-if='isBuyerRole')
            div.rowElements(style='font-weight:600') Totals
            div.rowElements
            div.rowElements
            div.rowElements
            div.rowElements {{ buyerTotals.totalPlays.toLocaleString() }}
            div.rowElements.totals-accent {{ formatCurrency(buyerTotals.totalCost) }}
          md-table-pagination(
            :md-size='currentLicensingPagination.size',
            :md-total='isContentOwnerRole ? totalLicensedMediaItems : totalBuyerRows',
            :md-page='currentLicensingPagination.page',
            md-label='Rows',
            md-separator='of',
            :md-page-options='[10, 25, 50, 100]',
            @pagination='onLicensingPageChanged'
          )
        .no-data(v-else-if='!loadingLicensed')
          md-icon.md-accent(md-iconset='fa fa-info-circle')
          span No licensing data for selected filters
      .billing-information(v-if='isBuyerRole')
        .billing-information__title Billing Information
        .billing-information__text
          | These are the costs for videos you've licensed from other creators. The cost is calculated based on the number of plays (impressions) your audience generates, divided by 1,000 and multiplied by the CPM (cost per thousand impressions) listed. Each video may appear in multiple distributions (channels), and costs are calculated separately per distribution.

      //- Rental Revenue — shown once report is available, no loader needed
      div(v-if='report && isContentOwnerRole')
        h3.section-title Rental Revenue
        .table(v-if='rentalRows.length > 0 || rentalPagination.totalCount > 0')
          .titleLine.styleRow
            p.rowElements DATE
            p.rowElements VIDEO NAME
            p.rowElements PRICE
            p.rowElements SOURCE
            p.rowElements PLATFORM FEE
            p.rowElements RHEBUS COMMISSION
            p.rowElements NET EARNINGS
          .styleRow.colorHover(v-for='(row, idx) in rentalRows', :key='idx')
            div.rowElements {{ formatDate(row.date) }}
            div.rowElements {{ row.name || row.videoName || '—' }}
            div.rowElements {{ formatCurrency(row.price) }}
            div.rowElements {{ row.source || '—' }}
            div.rowElements {{ formatCurrency(row.platformFee) }}
            div.rowElements {{ formatCurrency(row.rhebusCommission) }}
            div.rowElements {{ formatCurrency(row.netEarnings) }}
          .styleRow.totals-row
            div.rowElements(style='font-weight:600') Totals
            div.rowElements
            div.rowElements {{ formatCurrency(rentalTotals.price) }}
            div.rowElements
            div.rowElements -{{ formatCurrency(rentalTotals.platformFee) }}
            div.rowElements -{{ formatCurrency(rentalTotals.rhebusCommission) }}
            div.rowElements {{ formatCurrency(rentalTotals.netEarnings) }}
          md-table-pagination(
            :md-size='currentRentalPagination.size',
            :md-total='rentalPagination.totalCount',
            :md-page='currentRentalPagination.page',
            md-label='Rows',
            md-separator='of',
            :md-page-options='[10, 25, 50, 100]',
            @pagination='onRentalPageChanged'
          )
        .no-data(v-else-if='!loadingLicensed')
          md-icon.md-accent(md-iconset='fa fa-info-circle')
          span No rental data for selected filters
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';
import stitchLoader from '../Loader';
import reportService from '../../services/reportService';
import { generateMonths, generateYears } from './dateUtils';

export default {
  components: { stitchLoader },
  computed: {
    ...mapState({
      siteuser: state => state.user.siteuser
    }),
    canSearch() {
      return !!this.filters.month && !!this.filters.year;
    },
    isContentOwnerRole() {
      return this.activeRole === 'content-owner';
    },
    isBuyerRole() {
      return this.activeRole === 'buyer';
    },
    currentPeriodLabel() {
      return moment().month(this.filters.month - 1).year(this.filters.year).format('MMMM YYYY');
    },
    summary() {
      return this.report && this.report.summary
        ? this.report.summary
        : { licensingGross: 0, rhebusCommission: 0, netEarnings: 0, totalWithRentals: 0 };
    },
    searchParamsLicensed() {
      const params = {
        year: this.filters.year,
        month: this.filters.month,
        page: this.currentLicensingPagination.page,
        pageSize: this.currentLicensingPagination.size,
        rentalPage: this.currentRentalPagination.page,
        rentalPageSize: this.currentRentalPagination.size
      };
      if (this.debouncedLicensingSearchText) params.name = this.debouncedLicensingSearchText;
      if (this.filters.licenseeAccountId) params.licenseeAccountId = this.filters.licenseeAccountId;
      if (this.filters.distribution) params.distribution = this.filters.distribution;
      if (this.sortKey) {
        params.sortKey = this.sortKey;
        params.sortDirection = this.sortDirection;
      }
      return params;
    },
    searchParamsBuyer() {
      const params = {
        year: this.filters.year,
        month: this.filters.month,
        page: this.currentLicensingPagination.page,
        pageSize: this.currentLicensingPagination.size
      };
      if (this.debouncedLicensingSearchText) params.name = this.debouncedLicensingSearchText;
      if (this.filters.contentOwner) params.contentOwner = this.filters.contentOwner;
      if (this.filters.distribution) params.distribution = this.filters.distribution;
      if (this.sortKey) {
        params.sortKey = this.sortKey;
        params.sortDirection = this.sortDirection;
      }
      return params;
    },
    licenseeOptions() {
      const rows = (this.report && this.report.licensingRows) || [];
      const seen = new Map();
      for (const r of rows) {
        if (r.licenseeAccountId && r.licensee && !seen.has(r.licenseeAccountId)) {
          seen.set(r.licenseeAccountId, r.licensee);
        }
      }
      return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
    },
    distributionOptions() {
      const rows = (this.report && this.report.licensingRows) || [];
      const seen = new Map();
      for (const r of rows) {
        for (const d of (r.distributions || [])) {
          if (d && d.id) seen.set(d.id.toString(), d.name || '');
        }
      }
      return Array.from(seen.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
    },
    buyerTotals() {
      return this.buyerAllRowsTotals;
    },
    totalBuyerRows() {
      return this.totalBuyerLicensedItems;
    },
    activeColumns() {
      if (this.isContentOwnerRole) {
        return [
          { label: 'VIDEO NAME', getValue: item => item.name || item.videoName || '—' },
          { label: 'BUYER', getValue: item => item.licensee || '—' },
          { label: 'CPM', getValue: item => this.displayCurrency(item.cpm) },
          { label: 'PLAYS', sortKey: 'plays', getValue: item => this.displayPlays(item.plays) },
          { label: 'GROSS', sortKey: 'gross', getValue: item => this.displayCurrency(item.gross) },
          { label: `RHEBUS COMMISSION`, sortKey: 'rhebusCommission', getValue: item => this.displayCurrency(item.rhebusCommission) },
          { label: `NET EARNINGS`, sortKey: 'net', getValue: item => this.displayCurrency(item.net) }
        ];
      }
      return [
        { label: 'VIDEO NAME', sortKey: 'name', getValue: item => item.name || '—' },
        { label: 'CONTENT OWNER', sortKey: 'contentOwner', getValue: item => item.contentOwner || '—' },
        { label: 'DISTRIBUTION', getValue: item => (item.distributions || []).map(d => d.name || d).join(', ') || '—' },
        { label: 'CPM', getValue: item => this.displayCurrency(item.cpm) },
        { label: 'PLAYS', sortKey: 'plays', getValue: item => this.displayPlays(item.plays) },
        { label: 'TOTAL COST', sortKey: 'gross', getValue: item => this.displayCurrency(item.gross) }
      ];
    },
    activeRows() {
      return this.isContentOwnerRole ? this.licensedMediaItems : this.buyerLicensedItems;
    },
    rentalTotals() {
      return this.serverRentalTotals;
    }
  },
  data() {
    const now = new Date();
    return {
      months: generateMonths(),
      years: generateYears(),
      filters: {
        month: now.getMonth() + 1,
        year: now.getFullYear(),
        licenseeAccountId: '',
        contentOwner: '',
        distribution: ''
      },
      activeRole: 'content-owner',
      licensingSearchText: '',
      debouncedLicensingSearchText: '',
      licensingSearchDebounceTimer: null,
      sortKey: null,
      sortDirection: 'asc',
      report: null,
      feeConfig: { licenseFeeRate: 0.30, rentalFeeRate: 0.30 },
      licensedMediaItems: [],
      totalLicensedMediaItems: 0,
      licensingTotals: { plays: 0, gross: 0, rhebusCommission: 0, net: 0 },
      buyerLicensedItems: [],
      totalBuyerLicensedItems: 0,
      buyerAllRowsTotals: { totalPlays: 0, totalCost: 0 },
      buyerSummary: { totalLicensingCost: 0, totalPlays: 0, videosLicensed: 0, averageCpm: 0 },
      buyerContentOwners: [],
      buyerDistributions: [],
      rentalRows: [],
      serverRentalTotals: { price: 0, platformFee: 0, rhebusCommission: 0, netEarnings: 0 },
      rentalPagination: { totalCount: 0, totalPages: 0 },
      currentRentalPagination: { page: 1, size: 10 },
      loading: false,
      loadingLicensed: false,
      generating: false,
      currentLicensingPagination: {
        page: 1,
        size: 10
      }
    };
  },
  watch: {
    licensingSearchText() {
      if (this.licensingSearchDebounceTimer) {
        clearTimeout(this.licensingSearchDebounceTimer);
      }
      this.licensingSearchDebounceTimer = setTimeout(() => {
        this.debouncedLicensingSearchText = this.licensingSearchText;
        this.currentLicensingPagination.page = 1;
        if (this.isContentOwnerRole) {
          this.fetchLicensedMediaItems();
        } else if (this.isBuyerRole) {
          this.fetchBuyerLicensedItems();
        }
      }, 500);
    }
  },
  methods: {
    displayCurrency(v) {
      return v != null ? this.formatCurrency(v) : '—';
    },
    displayPlays(v) {
      return v != null ? v.toLocaleString() : '—';
    },
    onColumnHeaderClick(col) {
      if (col.sortKey) this.toggleSort(col.sortKey);
    },
    isSuperAdmin() {
      return this.siteuser &&
        this.siteuser.authorization &&
        this.siteuser.authorization.roles &&
        this.siteuser.authorization.roles.includes('SuperAdmin');
    },
    formatCurrency(value) {
      const n = parseFloat(value) || 0;
      return `$${n.toFixed(2)}`;
    },
    formatDate(value) {
      return value ? moment.utc(value).format('MM/DD/YYYY') : '—';
    },
    prevPeriod() {
      let { month, year } = this.filters;
      month -= 1;
      if (month < 1) { month = 12; year -= 1; }
      this.filters.month = month;
      this.filters.year = year;
      this.search();
    },
    nextPeriod() {
      let { month, year } = this.filters;
      month += 1;
      if (month > 12) { month = 1; year += 1; }
      this.filters.month = month;
      this.filters.year = year;
      this.search();
    },
    onPeriodChanged() {
      this.search();
    },
    setRole(role) {
      this.activeRole = role;
      this.sortKey = null;
      this.sortDirection = 'asc';
      this.filters.licenseeAccountId = '';
      this.filters.contentOwner = '';
      this.filters.distribution = '';
      this.currentLicensingPagination.page = 1;
      if (this.isContentOwnerRole) {
        this.fetchLicensedMediaItems();
      } else if (this.isBuyerRole) {
        this.fetchBuyerLicensedItems();
      }
    },
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDirection = 'asc';
      }
      this.currentLicensingPagination.page = 1;
      if (this.isContentOwnerRole) {
        this.fetchLicensedMediaItems();
      } else if (this.isBuyerRole) {
        this.fetchBuyerLicensedItems();
      }
    },
    sortIconFor(key) {
      if (this.sortKey !== key) return 'fa fa-sort';
      return this.sortDirection === 'asc' ? 'fa fa-sort-up' : 'fa fa-sort-down';
    },
    applyLicenseeFilter() {
      this.currentLicensingPagination.page = 1;
      this.fetchLicensedMediaItems();
    },
    applyBuyerFilters() {
      this.currentLicensingPagination.page = 1;
      this.fetchBuyerLicensedItems();
    },
    onLicensingPageChanged(paginationParams) {
      this.currentLicensingPagination = paginationParams;
      if (this.isContentOwnerRole) {
        this.fetchLicensedMediaItems();
      } else if (this.isBuyerRole) {
        this.fetchBuyerLicensedItems();
      }
    },
    onRentalPageChanged(paginationParams) {
      this.currentRentalPagination = paginationParams;
      this.fetchLicensedMediaItems();
    },
    resetLicensingPagination() {
      this.currentLicensingPagination.page = 1;
      this.currentRentalPagination.page = 1;
    },
    async fetchLicensedMediaItems() {
      if (!this.canSearch || !this.isContentOwnerRole) return;
      this.loadingLicensed = true;
      try {
        const res = await reportService.getLicensingReport(this.searchParamsLicensed);
        this.licensedMediaItems = res.data.mediaItems || [];
        this.totalLicensedMediaItems = (res.data.pagination && res.data.pagination.totalCount) || 0;
        this.report = res.data.report || null;
        if (res.data.feeConfig) this.feeConfig = res.data.feeConfig;
        if (res.data.licensingTotals) this.licensingTotals = res.data.licensingTotals;
        this.rentalRows = res.data.rentalRows || [];
        if (res.data.rentalTotals) this.serverRentalTotals = res.data.rentalTotals;
        if (res.data.rentalPagination) this.rentalPagination = res.data.rentalPagination;
      } catch (error) {
        console.error('Error fetching licensed media items', error);
      } finally {
        this.loadingLicensed = false;
      }
    },
    async fetchBuyerLicensedItems() {
      if (!this.canSearch || !this.isBuyerRole) return;
      this.loadingLicensed = true;
      try {
        const res = await reportService.getBuyerLicensingReport(this.searchParamsBuyer);
        this.buyerLicensedItems = res.data.mediaItems || [];
        this.totalBuyerLicensedItems = (res.data.pagination && res.data.pagination.totalCount) || 0;
        this.buyerSummary = res.data.summary || { totalLicensingCost: 0, totalPlays: 0, videosLicensed: 0, averageCpm: 0 };
        if (res.data.totals) this.buyerAllRowsTotals = res.data.totals;
        if (res.data.availableContentOwners) this.buyerContentOwners = res.data.availableContentOwners;
        if (res.data.availableDistributions) this.buyerDistributions = res.data.availableDistributions;
      } catch (error) {
        console.error('Error fetching buyer licensed items', error);
      } finally {
        this.loadingLicensed = false;
      }
    },
    search() {
      if (!this.canSearch) return;
      this.loading = true;
      this.report = null;
      this.resetLicensingPagination();
      if (this.isContentOwnerRole) {
        this.fetchLicensedMediaItems().finally(() => { this.loading = false; });
      } else if (this.isBuyerRole) {
        this.fetchBuyerLicensedItems().finally(() => { this.loading = false; });
      }
    },
    generateReport() {
      if (!this.canSearch) return;
      this.generating = true;
      reportService
        .generateLicensingReport({ year: this.filters.year, month: this.filters.month })
        .then(() => {
          return this.search();
        })
        .catch(err => {
          console.error('Error generating licensing report', err);
        })
        .finally(() => {
          this.generating = false;
        });
    }
  },
  mounted() {
    this.search();
  },
  beforeDestroy() {
    if (this.licensingSearchDebounceTimer) {
      clearTimeout(this.licensingSearchDebounceTimer);
    }
  }
};
</script>

<style lang="scss" scoped>

.fa-chevron-left,
.fa-chevron-right,
.fa-calendar-alt,
.fa-sync-alt {
  color: #fff !important;
  font-size: 14px !important;
  position: relative !important;
  top: 4px !important;
}

// Shared flex + align-items: center pattern
.header-row,
.header-right,
.period-control,
.licensing-search-filter-row,
.licensee-filter,
.styleRow,
.no-data {
  display: flex;
  align-items: center;
}

.rowElements.sortable {
  display: block !important;

  & > .md-icon {
    margin-left: 4px;
  }
}

.on-left {
  margin-top: -5px;
  margin-right: 5px;
}
.content-table {
  width: 100%;
  margin-top: 20px;
  padding: 10px;
  margin: 0;
  border-radius: 0;
  box-shadow: none;
}
.header-row {
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 8px;
}
.header-right {
  gap: 8px;
  flex-wrap: wrap;
}
.period-control {
  gap: 2px;
}
.period-label-text {
  font-weight: 500;
  margin-right: 4px;
}
.current-period-label {
  font-weight: 600;
  white-space: nowrap;
  margin: 0 4px;
}
.period-picker-popup {
  padding: 16px;
  min-width: 100px;
}
.role-tabs {
  display: flex;
  border-bottom: 2px solid #e0e0e0;
  margin: 8px 0;
}
.role-tab {
  padding: 10px 24px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  font-weight: 600;
  letter-spacing: 0.5px;
  &.active {
    border-bottom-color: currentColor;
  }
}
.summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin: 20px 0 8px;
}
.summary-card {
  flex: 1;
  min-width: 160px;
  background: #4ea9dd;
  border: 1px solid #b0dde4;
  border-radius: 6px;
  padding: 14px 18px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.summary-label,
.summary-value {
  color: #fff !important;
}

.summary-label {
  min-height: 40px;
}

.summary-label {
  font-size: 12px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}
.summary-value {
  font-size: 22px;
  font-weight: 600;
  color: #333;
}
.section-title {
  margin: 24px 0 8px;
  font-weight: 600;
  font-size: 16px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 4px;
}
.licensing-search-filter-row {
  flex-wrap: wrap;
  gap: 16px;
  margin: 8px 0 0;
}
.search-input-container {
  flex: 1;
  min-width: 200px;
  max-width: 360px;
}
.licensee-filter {
  gap: 8px;
}
.licensee-label {
  font-weight: 500;
  white-space: nowrap;
}
.table {
  margin-top: 8px;
}
.styleRow {
  p {
    font-weight: 600;
  }
}
.titleLine {
  border-bottom: 1px solid rgba(153, 155, 155, 0.486);
  padding: 4px 0;
}
.rowElements {
  flex: 1;
  padding: 6px 16px 6px 8px;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &.sortable {
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;
    gap: 4px;
  }
}
.colorHover:hover {
  background-color: #eee;
}
.totals-row {
  border-top: 2px solid #ccc;
  background: #fafafa;
  font-weight: 600;
}
.totals-accent {
  color: #d42f2f;
}
.no-data {
  gap: 8px;
  padding: 20px;
  color: #666;
  font-size: 14px;
}
.billing-information {
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 6px;
  border: 1px solid #dfe5f1;
  background: #f8faff;

  &__title,
  &__text {
    font-size: 13px;
  }
  &__title {
    font-weight: 600;
    margin-bottom: 4px;
  }
  &__text {
    color: #4d5a73;
    line-height: 1.4;
  }
}
</style>



// WEBPACK FOOTER //
// src/shared-components/report/LicensingReportTable.vue